import type { ColorName } from "@catppuccin/palette";

export type ColorScheme = "light" | "dark";
export type Flavor = "latte" | "mocha";

/** Catppuccin flavor used for each color scheme. */
export const FLAVOR_FOR_SCHEME: Record<ColorScheme, Flavor> = {
  light: "latte",
  dark: "mocha",
};

/** Shiki theme ids (bundled with Shiki) for each color scheme. */
export const SHIKI_THEMES: Record<ColorScheme, `catppuccin-${Flavor}`> = {
  light: "catppuccin-latte",
  dark: "catppuccin-mocha",
};

/** Catppuccin accent colors, in palette order. Used for peer cursors and avatars. */
export const ACCENTS = [
  "rosewater",
  "flamingo",
  "pink",
  "mauve",
  "red",
  "maroon",
  "peach",
  "yellow",
  "green",
  "teal",
  "sky",
  "sapphire",
  "blue",
  "lavender",
] as const satisfies readonly ColorName[];

export type Accent = (typeof ACCENTS)[number];

export function isAccent(value: string): value is Accent {
  return (ACCENTS as readonly string[]).includes(value);
}

/** Stable accent for a client, so a peer keeps the same color across sessions. */
export function colorFor(clientId: string): Accent {
  // FNV-1a, 32-bit.
  let hash = 0x811c9dc5;
  for (let i = 0; i < clientId.length; i++) {
    hash ^= clientId.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return ACCENTS[(hash >>> 0) % ACCENTS.length] as Accent;
}

/** CSS reference to an accent that follows the active flavor. */
export function accentVar(accent: Accent): string {
  return `var(--cs-${accent})`;
}
